import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { CriarSolicitacaoDto, ItemSolicitacaoDto } from '../../delivery.types';
import { DeliveryService } from '../../services/deliveryService';
import { useDeliveryStore } from '../../store/deliveryStore';
import {
  apenasDigitos,
  buscarCep,
  fmtMoeda,
  mascararCelular,
  mascararCep,
} from '../../utils';

interface Endereco {
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
}

const ENDERECO_VAZIO: Endereco = {
  cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '',
};

export default function StepCheckout() {
  const {
    unidade, carrinho,
    nomeContato, setNomeContato,
    whatsappContato, setWhatsappContato,
    tipoRetirada, setTipoRetirada,
    setEnderecoEntrega,
    codigoTipoTitulo, setCodigoTipoTitulo,
    observacaoGeral, setObservacaoGeral,
    setCodigoSolicitacao,
    setStep,
    totalComTaxa,
  } = useDeliveryStore();

  const [enviando, setEnviando] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [endereco, setEndereco] = useState<Endereco>(ENDERECO_VAZIO);

  function setEnd(campo: keyof Endereco, valor: string) {
    setEndereco((prev) => ({ ...prev, [campo]: valor }));
  }

  async function onCepCompleto(cepMascarado: string) {
    const digitos = apenasDigitos(cepMascarado);
    if (digitos.length !== 8) return;
    setBuscandoCep(true);
    try {
      const data = await buscarCep(cepMascarado);
      if (!data) { toast.error('CEP não encontrado.'); return; }
      setEndereco((prev) => ({
        ...prev,
        logradouro: prev.logradouro || data.logradouro,
        bairro:     prev.bairro     || data.bairro,
        cidade:     prev.cidade     || data.localidade,
        uf:         prev.uf         || data.uf,
        // complemento só preenche se vier do CEP e campo estiver vazio
        complemento: prev.complemento || data.complemento || '',
      }));
    } finally {
      setBuscandoCep(false);
    }
  }

  function montarEnderecoString(): string {
    const partes = [
      endereco.logradouro,
      endereco.numero ? `nº ${endereco.numero}` : '',
      endereco.complemento,
      endereco.bairro,
      endereco.cidade && endereco.uf ? `${endereco.cidade}/${endereco.uf}` : endereco.cidade,
      endereco.cep ? `CEP ${endereco.cep}` : '',
    ].filter(Boolean);
    return partes.join(', ');
  }

  const precisaEntrega = tipoRetirada === 'ENTREGA';

  const enderecoValido = !precisaEntrega || (
    apenasDigitos(endereco.cep).length === 8 &&
    endereco.logradouro.trim().length > 0 &&
    endereco.numero.trim().length > 0 &&
    endereco.cidade.trim().length > 0
  );

  const podeEnviar =
    nomeContato.trim().length >= 2 &&
    apenasDigitos(whatsappContato).length >= 10 &&
    enderecoValido &&
    codigoTipoTitulo != null &&
    carrinho.length > 0;

  async function enviar() {
    if (!unidade || !podeEnviar) return;
    setEnviando(true);
    try {
      const endStr = precisaEntrega ? montarEnderecoString() : undefined;
      setEnderecoEntrega(endStr ?? '');

      const itens: ItemSolicitacaoDto[] = carrinho.map((item) => {
        // converte grupoSelecoes em complementos ADICIONAL com preço correto
        const compsGrupo: typeof item.complementos[0][] = [];
        for (const g of item.grupoSelecoes) {
          if (!g.itens.length) continue;
          if (g.estrategiaPreco === 'MAIOR_VALOR') {
            const maxVal = Math.max(...g.itens.map((i) => i.valorAdicional));
            let maxAtribuido = false;
            for (const gi of g.itens) {
              compsGrupo.push({
                codigoProduto: gi.codigoProduto,
                descricaoProduto: gi.descricao,
                tipo: 'ADICIONAR',
                quantidade: 1,
                valorUnitario: !maxAtribuido && gi.valorAdicional === maxVal ? maxVal : 0,
              } as any);
              if (!maxAtribuido && gi.valorAdicional === maxVal) maxAtribuido = true;
            }
          } else {
            for (const gi of g.itens) {
              compsGrupo.push({
                codigoProduto: gi.codigoProduto,
                descricaoProduto: gi.descricao,
                tipo: 'ADICIONAR',
                quantidade: 1,
                valorUnitario: gi.valorAdicional,
              } as any);
            }
          }
        }

        return {
          codigoProduto: item.codigoProduto,
          descricaoProduto: item.descricaoProduto,
          quantidade: item.quantidade,
          valorUnitario: item.valorUnitario,
          observacao: item.observacao || undefined,
          complementos: [
            ...item.complementos.map((c) => ({
              codigoProduto: c.codigoProduto,
              descricaoProduto: c.descricaoProduto,
              tipo: c.tipo,
              quantidade: c.quantidade,
              valorUnitario: c.valorUnitario,
            })),
            ...compsGrupo,
          ],
        };
      });

      const dto: CriarSolicitacaoDto = {
        codigoUnidade: unidade.codigo,
        nomeContato,
        whatsappContato,
        tipoRetirada,
        enderecoEntrega: endStr,
        codigoTipoTitulo: codigoTipoTitulo!,
        observacaoGeral: observacaoGeral || undefined,
        itens,
      };

      const resp = await DeliveryService.criarSolicitacao(dto);
      setCodigoSolicitacao(resp.codigo);
      setStep(4);
    } catch {
      toast.error('Erro ao enviar pedido. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Box display="flex" flexDirection="column" gap={2.5} pb={4}>
      {/* ── Seus dados ─────────────────────────────────────────────── */}
      <Typography variant="subtitle1" fontWeight={700}>Seus dados</Typography>

      <TextField
        label="Seu nome completo"
        fullWidth
        value={nomeContato}
        onChange={(e) => setNomeContato(e.target.value)}
        required
        placeholder="Ex: João Silva"
      />

      <TextField
        label="WhatsApp / Celular"
        fullWidth
        value={whatsappContato}
        onChange={(e) => setWhatsappContato(mascararCelular(e.target.value))}
        required
        placeholder="(11) 99999-9999"
        slotProps={{ htmlInput: { inputMode: 'tel' } }}
      />

      {/* ── Forma de recebimento ────────────────────────────────────── */}
      {unidade?.permiteRetirada && (
        <>
          <Divider />
          <Typography variant="subtitle1" fontWeight={700}>Forma de recebimento</Typography>
          <RadioGroup
            value={tipoRetirada}
            onChange={(e) => setTipoRetirada(e.target.value as 'ENTREGA' | 'RETIRADA')}
          >
            <FormControlLabel value="ENTREGA" control={<Radio />} label="Entrega no meu endereço" />
            <FormControlLabel value="RETIRADA" control={<Radio />} label="Retirar no local" />
          </RadioGroup>
        </>
      )}

      {/* ── Endereço de entrega ─────────────────────────────────────── */}
      {precisaEntrega && (
        <>
          <Divider />
          <Typography variant="subtitle1" fontWeight={700}>Endereço de entrega</Typography>

          {/* CEP — primeiro campo */}
          <TextField
            label="CEP"
            fullWidth
            required
            value={endereco.cep}
            onChange={(e) => {
              const mascarado = mascararCep(e.target.value);
              setEnd('cep', mascarado);
              if (apenasDigitos(mascarado).length === 8) onCepCompleto(mascarado);
            }}
            placeholder="00000-000"
            slotProps={{
              htmlInput: { inputMode: 'numeric' },
              input: {
                endAdornment: buscandoCep
                  ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
                  : undefined,
              },
            }}
          />

          <TextField
            label="Logradouro (rua, avenida…)"
            fullWidth
            required
            value={endereco.logradouro}
            onChange={(e) => setEnd('logradouro', e.target.value)}
            placeholder="Ex: Rua das Flores"
          />

          <Box display="flex" gap={1.5}>
            <TextField
              label="Número"
              required
              value={endereco.numero}
              onChange={(e) => setEnd('numero', e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              sx={{ width: 120, flexShrink: 0 }}
            />
            <TextField
              label="Complemento"
              fullWidth
              value={endereco.complemento}
              onChange={(e) => setEnd('complemento', e.target.value)}
              placeholder="Apto, bloco…"
            />
          </Box>

          <TextField
            label="Bairro"
            fullWidth
            value={endereco.bairro}
            onChange={(e) => setEnd('bairro', e.target.value)}
          />

          <Box display="flex" gap={1.5}>
            <TextField
              label="Cidade"
              required
              fullWidth
              value={endereco.cidade}
              onChange={(e) => setEnd('cidade', e.target.value)}
            />
            <TextField
              label="UF"
              value={endereco.uf}
              onChange={(e) => setEnd('uf', e.target.value.toUpperCase().slice(0, 2))}
              sx={{ width: 80, flexShrink: 0 }}
              slotProps={{ htmlInput: { maxLength: 2 } }}
            />
          </Box>
        </>
      )}

      {/* ── Pagamento ───────────────────────────────────────────────── */}
      <Divider />
      <Typography variant="subtitle1" fontWeight={700}>Forma de pagamento</Typography>
      <FormControl fullWidth required>
        <InputLabel>Como vai pagar?</InputLabel>
        <Select
          value={codigoTipoTitulo ?? ''}
          label="Como vai pagar?"
          onChange={(e) => setCodigoTipoTitulo(e.target.value ? Number(e.target.value) : null)}
        >
          {(unidade?.tiposTitulo ?? []).map((t) => (
            <MenuItem key={t.codigo} value={t.codigo}>{t.descricao}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Alguma observação? (opcional)"
        fullWidth
        multiline
        rows={2}
        value={observacaoGeral}
        onChange={(e) => setObservacaoGeral(e.target.value)}
        placeholder="Ex: sem cebola, apartamento no 3º andar…"
      />

      {/* ── Total ───────────────────────────────────────────────────── */}
      <Divider />
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontWeight={700} variant="subtitle1">Total do pedido</Typography>
        <Typography fontWeight={800} variant="h6" color="primary">
          {fmtMoeda(totalComTaxa())}
        </Typography>
      </Box>

      <Box display="flex" gap={2}>
        <Button variant="outlined" fullWidth onClick={() => setStep(2)}>
          Voltar
        </Button>
        <Button
          variant="contained"
          fullWidth
          disabled={!podeEnviar || enviando}
          onClick={enviar}
          sx={{ py: 1.5 }}
        >
          {enviando ? <CircularProgress size={22} color="inherit" /> : 'Confirmar pedido'}
        </Button>
      </Box>
    </Box>
  );
}
